/**
 * Muster climb — free-play variant.
 * Persistent start deck (default starter), mid-run findings, end-of-run
 * inscribe-or-cut. Separate best depth from open endless.
 */
import { STARTER_OWNED_IDS } from "../../../shared/collection/starter";
import { CATALOG } from "../../../shared/deck/catalog";
import { PACK_GATED_UNTIL_OWNED } from "../../../shared/collection/packs";
import { copiesOwned } from "../../../shared/collection/storage";
import { verseById } from "../data/verses";
import { formatSurviveTime } from "./endlessScore";

const KEY = "sots-muster-v1";

/** Same size as the shared starter loadout. */
export const MUSTER_MIN = STARTER_OWNED_IDS.length; // 6
/** Room to grow without becoming a full library. */
export const MUSTER_MAX = 10;

export const DEFAULT_MUSTER_IDS: readonly string[] = [...STARTER_OWNED_IDS];

export type MusterSave = {
  version: 1;
  /** Persistent start deck (multiplicity = repeated ids). */
  muster: string[];
  /** Best survival seconds by difficulty. */
  bestByDifficulty: Partial<Record<string, number>>;
};

function defaultSave(): MusterSave {
  return {
    version: 1,
    muster: [...DEFAULT_MUSTER_IDS],
    bestByDifficulty: {},
  };
}

function sanitizeMuster(ids: readonly string[]): string[] {
  const out: string[] = [];
  for (const raw of ids) {
    const id = String(raw || "").trim();
    if (!id || !verseById(id)) continue;
    out.push(id);
    if (out.length >= MUSTER_MAX) break;
  }
  if (out.length < MUSTER_MIN) {
    // Top up from default without exceeding max
    for (const id of DEFAULT_MUSTER_IDS) {
      if (out.length >= MUSTER_MIN) break;
      out.push(id);
    }
  }
  return out;
}

function loadRaw(): MusterSave {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaultSave();
    const p = JSON.parse(raw) as Partial<MusterSave>;
    const muster = sanitizeMuster(
      Array.isArray(p.muster) ? p.muster.map(String) : DEFAULT_MUSTER_IDS,
    );
    return {
      version: 1,
      muster,
      bestByDifficulty:
        p.bestByDifficulty && typeof p.bestByDifficulty === "object"
          ? { ...p.bestByDifficulty }
          : {},
    };
  } catch {
    return defaultSave();
  }
}

function writeSave(s: MusterSave): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* quota */
  }
}

export function getMusterDeck(): string[] {
  return [...loadRaw().muster];
}

export function getMusterSize(): number {
  return loadRaw().muster.length;
}

export function canInscribeToMuster(): boolean {
  return getMusterSize() < MUSTER_MAX;
}

export function canRemoveFromMuster(): boolean {
  return getMusterSize() > MUSTER_MIN;
}

/** True when muster matches the default starter (order-insensitive multiset). */
export function isMusterDefault(): boolean {
  const a = [...getMusterDeck()].sort().join("\0");
  const b = [...DEFAULT_MUSTER_IDS].sort().join("\0");
  return a === b;
}

/**
 * Inscribe a verse found this run into the persistent muster.
 * Returns false if at max or id invalid.
 */
export function inscribeToMuster(verseId: string): boolean {
  if (!verseById(verseId)) return false;
  const s = loadRaw();
  if (s.muster.length >= MUSTER_MAX) return false;
  s.muster.push(verseId);
  writeSave(s);
  return true;
}

/**
 * Remove one copy of verseId from muster.
 * Returns false if at min or id not present.
 */
export function removeFromMuster(verseId: string): boolean {
  const s = loadRaw();
  if (s.muster.length <= MUSTER_MIN) return false;
  const i = s.muster.indexOf(verseId);
  if (i < 0) return false;
  s.muster.splice(i, 1);
  writeSave(s);
  return true;
}

/** Reset muster to the default starter deck. Best depth is kept. */
export function resetMusterDeck(): void {
  const s = loadRaw();
  s.muster = [...DEFAULT_MUSTER_IDS];
  writeSave(s);
}

export function getMusterBest(difficulty: string): number {
  return loadRaw().bestByDifficulty[difficulty] ?? 0;
}

export function recordMusterScore(
  difficulty: string,
  elapsed: number,
): { best: number; isNewBest: boolean } {
  const data = loadRaw();
  const prev = data.bestByDifficulty[difficulty] ?? 0;
  const isNewBest = elapsed > prev + 0.05;
  if (isNewBest) {
    data.bestByDifficulty[difficulty] = elapsed;
    writeSave(data);
  }
  return { best: Math.max(prev, elapsed), isNewBest };
}

export function formatMusterSummary(): string {
  const n = getMusterSize();
  return `${n} cards · min ${MUSTER_MIN} · max ${MUSTER_MAX}`;
}

/** Unique finds this run (preserve first-seen order). */
export function uniqueFindings(findings: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of findings) {
    if (!id || seen.has(id) || !verseById(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function shuffleInPlace<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = t;
  }
  return arr;
}

/**
 * Build a 3-card provision offer for mid-run findings.
 * Prefers verses not already thick in the field deck; skips campaign-gated
 * signatures until owned in the collection.
 */
export function buildMusterFindingOptions(
  fieldDeck: readonly string[],
  count = 3,
): string[] {
  const fieldCounts = new Map<string, number>();
  for (const id of fieldDeck) {
    fieldCounts.set(id, (fieldCounts.get(id) ?? 0) + 1);
  }

  const pool: string[] = [];
  for (const v of CATALOG) {
    if (!verseById(v.id)) continue;
    if (
      PACK_GATED_UNTIL_OWNED.includes(v.id) &&
      copiesOwned(v.id) < 1
    ) {
      continue;
    }
    pool.push(v.id);
  }
  shuffleInPlace(pool);

  // Prefer cards with fewer copies already in the field deck
  pool.sort((a, b) => (fieldCounts.get(a) ?? 0) - (fieldCounts.get(b) ?? 0));

  const out: string[] = [];
  for (const id of pool) {
    if (out.includes(id)) continue;
    out.push(id);
    if (out.length >= count) break;
  }
  return out;
}

/**
 * When the field deck is full, offer unique cards already in the deck to lay aside.
 */
export function buildMusterCullOptions(
  fieldDeck: readonly string[],
  count = 3,
): string[] {
  const unique = [...new Set(fieldDeck.filter((id) => !!verseById(id)))];
  shuffleInPlace(unique);
  return unique.slice(0, Math.max(1, count));
}

export function musterEndLead(opts: {
  canAdd: boolean;
  canRemove: boolean;
  findCount: number;
}): string {
  if (opts.canAdd && opts.canRemove && opts.findCount > 0) {
    return "Inscribe one finding into your start deck, remove one card, or keep as-is.";
  }
  if (opts.canAdd && opts.findCount > 0) {
    return "Muster is at minimum size — inscribe a finding or keep as-is.";
  }
  if (opts.canRemove) {
    return opts.findCount > 0
      ? "Muster is full — remove a card to make room next run, or keep as-is."
      : "Remove a card from your start deck, or keep as-is.";
  }
  return "Keep your muster as-is.";
}

export { formatSurviveTime };
